import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { X, Search, Trash2, ArrowUpDown, ChevronUp, ChevronDown, Filter, RefreshCw, Star, Tag, Plus, Percent, Printer } from 'lucide-react';
import { toast } from 'sonner';
import { LabelPreviewModal } from './LabelPreviewModal';
import { printCatalogPOS } from './labelUtils';

const API_URL = window.location.origin;
const authHdr = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${localStorage.getItem('admin_token')}` });

const fmt = (v) => v != null ? Number(v).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' }) : '—';

const QUALITY_FALLBACK = {
  origine:          { label: 'Origine / OEM',    icon: '🏆', color: 'text-yellow-400' },
  premium_oled:     { label: 'Premium OLED',      icon: '💎', color: 'text-blue-400' },
  premium_plus_fhd: { label: 'Premium Plus FHD',  icon: '✨', color: 'text-purple-400' },
  premium:          { label: 'Premium',           icon: '⭐', color: 'text-yellow-300' },
  original_refurb:  { label: 'Original Refurb',   icon: '♻️', color: 'text-green-400' },
  entree_gamme:     { label: 'Entrée de gamme',   icon: '💧', color: 'text-slate-400' },
};

// Onglets de catégories (avec mots-clés pour détection automatique)
const TABS = [
  { id: 'all',        label: 'Tous',              icon: '📋', keywords: null },
  { id: 'ecran',      label: 'Écran',              icon: '📱', keywords: ['écran', 'ecran', 'screen', 'display', 'oled', 'lcd', 'tft', 'amoled'] },
  { id: 'batterie',   label: 'Batterie',           icon: '🔋', keywords: ['batterie', 'battery', 'accu'] },
  { id: 'connecteur', label: 'Connecteur',         icon: '🔌', keywords: ['connecteur', 'connector', 'charge', 'lightning', 'usb', 'type-c', 'typec'] },
  { id: 'arriere',    label: 'Arrière iPhone',     icon: '📲', keywords: ['arrière', 'arriere', 'dos', 'vitre arrière', 'vitre arriere', 'back', 'coque'] },
  { id: 'autres',     label: 'Autres réparations', icon: '🔧', keywords: [] },
];

const ALL_KNOWN_KW = TABS
  .filter(t => t.id !== 'all' && t.id !== 'autres' && t.keywords?.length)
  .flatMap(t => t.keywords);

const COLS = [
  { key: 'brand_label',       label: 'Marque',     minW: 'min-w-[110px]' },
  { key: 'model_label',       label: 'Modèle',     minW: 'min-w-[130px]' },
  { key: 'part_label',        label: 'Catégorie',  minW: 'min-w-[100px]' },
  { key: 'quality',           label: 'Qualité',    minW: 'min-w-[150px]' },
  { key: 'prix_achat_ht',     label: 'Achat HT',   align: 'right', minW: 'min-w-[90px]' },
  { key: 'prix_vente_custom', label: 'Prix Admin', align: 'right', minW: 'min-w-[110px]' },
  { key: 'prix_vente_ttc',    label: 'Suggestion', align: 'right', minW: 'min-w-[110px]' },
  { key: 'taux_marge',        label: 'Marge',      align: 'right', minW: 'min-w-[80px]' },
];

export const CatalogModal = ({
  onLoadProduct,
  onClose,
  onNewProduct,
  qualityTypes = [],
  initialTab = 'all',
  initialBrand = '',
  initialSearch = '',
}) => {
  const [products, setProducts]       = useState([]);
  const [loading, setLoading]         = useState(false);
  const [search, setSearch]           = useState(initialSearch);
  const [filterQuality, setFilterQuality] = useState('');
  const [sortKey, setSortKey]         = useState('brand_label');
  const [sortDir, setSortDir]         = useState('asc');
  const [deleting, setDeleting]       = useState(null);
  const [activeTab, setActiveTab]     = useState(initialTab);
  const [printItem, setPrintItem]     = useState(null); // { product, type: null|'repair'|'sale' }
  const [activeBrand, setActiveBrand] = useState(initialBrand);
  const [inlineEdit, setInlineEdit]   = useState({});  // { [productId]: { open, prixHT, marge } }

  const TVA = 1.20; // 20%

  // Calcule le TTC custom depuis un prix HT + une marge HT en euros
  const calcCustomTTC = (prixHT, margeHT) => {
    const ht = parseFloat(prixHT);
    const m  = parseFloat(margeHT);
    if (!isNaN(ht) && ht > 0 && !isNaN(m) && m >= 0) {
      return Math.round((ht + m) * TVA * 100) / 100;
    }
    return null;
  };

  const toggleInline = (e, p) => {
    e.stopPropagation();
    setInlineEdit(prev => ({
      ...prev,
      [p.id]: { open: !prev[p.id]?.open, prixHT: String(p.prix_achat_ht || ''), marge: '' },
    }));
  };

  const saveMargin = async (e, p) => {
    e.stopPropagation();
    const edit = inlineEdit[p.id] || {};
    const prixHT = parseFloat(edit.prixHT);
    const ttc    = calcCustomTTC(edit.prixHT, edit.marge);
    if (!ttc || isNaN(prixHT) || prixHT <= 0) { toast.error('Vérifiez le Prix HT et la marge'); return; }
    try {
      await fetch(`${API_URL}/api/pricing/products/${p.id}`, {
        method: 'PUT', headers: authHdr(),
        body: JSON.stringify({ ...p, prix_achat_ht: prixHT, prix_vente_custom: ttc }),
      });
      await fetchProducts();
      setInlineEdit(prev => ({ ...prev, [p.id]: { open: false } }));
      toast.success('Marge enregistrée ✓');
    } catch { toast.error('Erreur sauvegarde'); }
  };

  // Quality map: dynamic types + fallback
  const qualityMap = useMemo(() => {
    const map = { ...QUALITY_FALLBACK };
    qualityTypes.forEach(q => { map[q.key] = { label: q.label, icon: q.icon, color: q.color || 'text-slate-300' }; });
    return map;
  }, [qualityTypes]);

  const allQualities = useMemo(() => {
    const keys = new Set([...Object.keys(QUALITY_FALLBACK), ...qualityTypes.map(q => q.key)]);
    return [...keys].map(k => ({ key: k, ...(qualityMap[k] || { label: k, icon: '?', color: 'text-slate-400' }) }));
  }, [qualityMap, qualityTypes]);

  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/pricing/products`, { headers: authHdr() });
      if (r.ok) setProducts(await r.json());
    } catch { toast.error('Erreur de chargement'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchProducts(); }, [fetchProducts]);

  // Reset brand filter when tab changes
  useEffect(() => { setActiveBrand(''); }, [activeTab]);

  // Accent-insensitive normalizer for search
  const normalize = (s) => (s || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  // Products visible in the current tab (used for brand sub-menu)
  const productsInTab = useMemo(() => {
    const tab = TABS.find(t => t.id === activeTab);
    if (!tab || tab.id === 'all') return products;
    return products.filter(p => {
      const partNorm = normalize(p.part_label);
      if (tab.id === 'autres') return !ALL_KNOWN_KW.some(kw => partNorm.includes(normalize(kw)));
      return tab.keywords?.some(kw => partNorm.includes(normalize(kw)));
    });
  }, [products, activeTab]);

  // Brands available in the current tab (for the sub-menu bar)
  const tabBrands = useMemo(() => {
    if (activeTab === 'all') return [];
    const brands = [...new Set(productsInTab.map(p => p.brand_label).filter(Boolean))];
    return brands.sort();
  }, [productsInTab, activeTab]);

  // ── Smart AND-logic filter (accent-insensitive) ──────────────────────────────
  // Splits query into terms. Every term must match at least one field (accent-aware).
  // "écran iPhone 11" OR "ecran iphone 11" → both work correctly.

  const filtered = useMemo(() => {
    const terms = normalize(search).trim().split(/\s+/).filter(Boolean);
    const tab = TABS.find(t => t.id === activeTab);

    return products
      .filter(p => {
        // AND search — every term must appear in at least one field (accent-insensitive)
        if (terms.length > 0) {
          const fields = [
            p.brand_label,
            p.model_label,
            p.part_label,
            qualityMap[p.quality]?.label,
          ].map(f => normalize(f));
          if (!terms.every(term => fields.some(f => f.includes(term)))) return false;
        }
        // Quality filter
        if (filterQuality && p.quality !== filterQuality) return false;
        // Brand sub-menu filter
        if (activeBrand && p.brand_label !== activeBrand) return false;
        // Tab filter
        if (activeTab !== 'all' && tab) {
          const partNorm = normalize(p.part_label);
          if (tab.id === 'autres') {
            if (ALL_KNOWN_KW.some(kw => partNorm.includes(normalize(kw)))) return false;
          } else if (tab.keywords?.length > 0) {
            if (!tab.keywords.some(kw => partNorm.includes(normalize(kw)))) return false;
          }
        }
        return true;
      })
      .sort((a, b) => {
        let av = a[sortKey] ?? 0;
        let bv = b[sortKey] ?? 0;
        if (sortKey === 'prix_vente_custom') {
          av = a.prix_vente_custom ?? a.prix_vente_ttc ?? 0;
          bv = b.prix_vente_custom ?? b.prix_vente_ttc ?? 0;
        }
        const cmp = typeof av === 'number' ? av - bv : String(av).localeCompare(String(bv), 'fr');
        return sortDir === 'asc' ? cmp : -cmp;
      });
  }, [products, search, filterQuality, activeBrand, sortKey, sortDir, activeTab, qualityMap]);

  const handleSort = (key) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Supprimer cette fiche ?')) return;
    setDeleting(id);
    try {
      await fetch(`${API_URL}/api/pricing/products/${id}`, { method: 'DELETE', headers: authHdr() });
      setProducts(p => p.filter(x => x.id !== id));
      toast.success('Fiche supprimée');
    } catch { toast.error('Erreur suppression'); }
    finally { setDeleting(null); }
  };

  const SortIcon = ({ col }) => {
    if (sortKey !== col) return <ArrowUpDown size={11} className="opacity-30" />;
    return sortDir === 'asc' ? <ChevronUp size={11} /> : <ChevronDown size={11} />;
  };

  const avgMarge = filtered.length > 0
    ? (filtered.reduce((a, p) => a + (p.taux_marge || 0), 0) / filtered.length).toFixed(1)
    : 0;

  return (<>
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-3">
      <div className="bg-gray-900 rounded-2xl w-full max-w-7xl flex flex-col border border-gray-700 shadow-2xl"
        style={{ height: '92vh' }}>

        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="flex items-center gap-4 px-6 py-4 border-b border-gray-700 shrink-0 bg-gray-800 rounded-t-2xl">
          <Tag size={20} className="text-amber-500 shrink-0" />
          <div className="flex-1">
            <h2 className="text-white font-bold text-xl">Catalogue Produits</h2>
            <p className="text-gray-400 text-sm">
              {loading ? 'Chargement...' : `${filtered.length} / ${products.length} fiches`}
              {' '}— cliquez une ligne pour charger dans le calculateur
            </p>
          </div>
          <button onClick={fetchProducts} disabled={loading} title="Actualiser le catalogue"
            data-testid="catalog-refresh-btn"
            className="flex items-center gap-2 px-3 py-2 bg-gray-700 hover:bg-gray-600 border border-gray-600 text-gray-300 rounded-xl transition-all text-sm disabled:opacity-50">
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            Actualiser
          </button>
          <button onClick={onClose} className="p-2 text-gray-400 hover:text-white rounded-xl hover:bg-gray-700 transition-all">
            <X size={20} />
          </button>
        </div>

        {/* ── Search + Quality filter ─────────────────────────────────────── */}
        <div className="flex items-center gap-3 px-5 py-3 border-b border-gray-700 shrink-0 bg-gray-900">
          <div className="relative flex-1">
            <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-500" />
            <input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder='Recherche intelligente : "écran iPhone 11" · "batterie Samsung S22"'
              data-testid="catalog-search-input"
              className="w-full bg-gray-800 border border-gray-600 text-white text-sm rounded-xl pl-10 pr-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-amber-400 placeholder-gray-500"
            />
            {search && (
              <button onClick={() => setSearch('')} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-200">
                <X size={13} />
              </button>
            )}
          </div>
          <div className="relative">
            <Filter size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 pointer-events-none" />
            <select value={filterQuality} onChange={e => setFilterQuality(e.target.value)}
              className="bg-gray-800 border border-gray-600 text-gray-200 text-sm rounded-xl pl-8 pr-4 py-2.5 focus:outline-none focus:ring-2 focus:ring-amber-400 appearance-none min-w-[180px]">
              <option value="">Toutes les qualités</option>
              {allQualities.map(q => (
                <option key={q.key} value={q.key}>{q.icon} {q.label}</option>
              ))}
            </select>
          </div>
          {(search || filterQuality) && (
            <button onClick={() => { setSearch(''); setFilterQuality(''); }}
              className="text-sm text-amber-400 hover:text-amber-300 whitespace-nowrap flex items-center gap-1">
              <X size={13} /> Effacer
            </button>
          )}
        </div>

        {/* ── Onglets catégories + bouton "+" ─────────────────────────────── */}
        <div className="flex items-center gap-1.5 px-5 py-2.5 border-b border-gray-700 shrink-0 bg-gray-800 overflow-x-auto">
          {TABS.map(tab => {
            const count = tab.id === 'all'
              ? products.length
              : tab.id === 'autres'
                ? products.filter(p => !ALL_KNOWN_KW.some(kw => (p.part_label || '').toLowerCase().includes(kw))).length
                : products.filter(p => tab.keywords?.some(kw => (p.part_label || '').toLowerCase().includes(kw))).length;
            const isActive = activeTab === tab.id;
            return (
              <button key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                data-testid={`tab-${tab.id}`}
                className={`shrink-0 flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold transition-all whitespace-nowrap
                  ${isActive
                    ? 'bg-amber-500 text-white shadow-sm'
                    : 'bg-gray-700 text-gray-400 border border-gray-600 hover:border-amber-400 hover:text-amber-500'}`}>
                <span>{tab.icon}</span>
                <span>{tab.label}</span>
                <span className={`px-1.5 py-0.5 rounded-full text-xs font-bold
                  ${isActive ? 'bg-white/20 text-white' : 'bg-gray-600 text-gray-400'}`}>
                  {count}
                </span>
              </button>
            );
          })}
          <div className="flex-1 min-w-4" />
          <button
            onClick={() => { onNewProduct?.(); onClose(); }}
            data-testid="catalog-new-product-btn"
            className="shrink-0 flex items-center gap-1.5 px-3.5 py-1.5 rounded-xl text-xs font-bold bg-amber-500 hover:bg-amber-400 text-white transition-all shadow-sm">
            <Plus size={13} /> Nouvelle fiche
          </button>
        </div>

        {/* ── Sous-menu marques (affiché quand un onglet catégorie est actif) ── */}
        {activeTab !== 'all' && tabBrands.length > 0 && (
          <div className="flex items-center gap-1.5 px-5 py-2 border-b border-gray-700 shrink-0 bg-gray-900 overflow-x-auto">
            <span className="text-gray-500 text-xs font-semibold shrink-0 mr-1">Marque :</span>
            <button
              onClick={() => setActiveBrand('')}
              data-testid="brand-filter-all"
              className={`shrink-0 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all whitespace-nowrap
                ${!activeBrand ? 'bg-gray-200 text-gray-900' : 'bg-gray-700 text-gray-400 hover:bg-gray-600'}`}>
              Toutes
            </button>
            {tabBrands.map(brand => (
              <button key={brand}
                onClick={() => setActiveBrand(activeBrand === brand ? '' : brand)}
                data-testid={`brand-filter-${brand.toLowerCase().replace(/\s+/g, '-')}`}
                className={`shrink-0 px-2.5 py-1 rounded-lg text-xs font-semibold transition-all whitespace-nowrap
                  ${activeBrand === brand
                    ? 'bg-gray-200 text-gray-900 shadow-sm'
                    : 'bg-gray-700 text-gray-400 border border-gray-600 hover:border-gray-400 hover:text-gray-200'}`}>
                {brand}
              </button>
            ))}
          </div>
        )}

        {/* ── Tableau ─────────────────────────────────────────────────────── */}
        <div className="flex-1 overflow-auto">
          <table className="w-full text-sm border-separate border-spacing-0">
            <thead className="sticky top-0 bg-gray-800 z-10">
              <tr>
                {COLS.map(col => (
                  <th key={col.key} onClick={() => handleSort(col.key)}
                    className={`px-4 py-3.5 font-semibold text-gray-400 cursor-pointer hover:text-gray-200 select-none whitespace-nowrap border-b border-gray-700
                      ${col.align === 'right' ? 'text-right' : 'text-left'} ${col.minW}`}>
                    <span className="inline-flex items-center gap-1.5">
                      {col.label === 'Prix Admin' && <Star size={11} className="text-amber-500 shrink-0" />}
                      {col.label}
                      <SortIcon col={col.key} />
                    </span>
                  </th>
                ))}
                <th className="px-4 py-3.5 text-right text-gray-400 font-semibold border-b border-gray-700">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={9} className="text-center py-16 text-gray-400">
                  <div className="flex flex-col items-center gap-3">
                    <div className="w-8 h-8 border-2 border-amber-500 border-t-transparent rounded-full animate-spin" />
                    Chargement...
                  </div>
                </td></tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={9} className="text-center py-16 text-gray-400 text-base">
                  {search || filterQuality || activeBrand
                    ? 'Aucun résultat pour ce filtre'
                    : 'Aucune fiche produit enregistrée'}
                </td></tr>
              )}
              {!loading && filtered.map((p, i) => {
                const q = qualityMap[p.quality] || { icon: '?', label: p.quality, color: 'text-gray-500' };
                const margin = p.taux_marge ?? 0;
                const hasCustom = p.prix_vente_custom != null && p.prix_vente_custom > 0;
                return (
                  <tr key={p.id}
                    onClick={() => { onLoadProduct(p); onClose(); }}
                    data-testid={`catalog-row-${p.id}`}
                    className={`cursor-pointer transition-colors group
                      ${i % 2 === 0 ? 'bg-gray-900 hover:bg-amber-500/10' : 'bg-gray-800/50 hover:bg-amber-500/10'}`}>
                    <td className="px-4 py-3.5 text-gray-100 font-semibold border-b border-gray-700">{p.brand_label || '—'}</td>
                    <td className="px-4 py-3.5 text-gray-400 border-b border-gray-700">{p.model_label || '—'}</td>
                    <td className="px-4 py-3.5 text-gray-500 border-b border-gray-700">{p.part_label || '—'}</td>
                    <td className="px-4 py-3.5 border-b border-gray-700">
                      <span className="inline-flex items-center gap-1.5 font-semibold text-sm text-gray-300">
                        <span>{q.icon}</span> {q.label}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-right text-gray-500 font-mono border-b border-gray-700">{fmt(p.prix_achat_ht)}</td>
                    {/* Prix Admin (custom) */}
                    <td className="px-4 py-2.5 text-right border-b border-gray-100 align-top" onClick={e => e.stopPropagation()}>
                      {/* Prix custom affiché */}
                      <div className="flex justify-end">
                        {hasCustom ? (
                          <span className="inline-flex items-center gap-1.5 font-black text-base text-amber-600"
                            style={{ animation: 'pricePulse 2s ease-in-out infinite' }}>
                            <Star size={12} className="text-amber-500 shrink-0" />
                            {fmt(p.prix_vente_custom)}
                          </span>
                        ) : (
                          <span className="text-gray-300 text-sm">—</span>
                        )}
                      </div>

                      {/* Bouton toggle marge (violet) */}
                      <div className="flex justify-end mt-1">
                        <button
                          onClick={e => toggleInline(e, p)}
                          data-testid={`margin-toggle-${p.id}`}
                          className={`flex items-center gap-0.5 text-[10px] font-bold px-1.5 py-0.5 rounded-md transition-all
                            ${inlineEdit[p.id]?.open
                              ? 'bg-purple-500 text-white'
                              : 'bg-purple-500/10 text-purple-400 hover:bg-purple-500/20 hover:text-purple-300 border border-purple-500/30'}`}>
                          <Percent size={9} /> Marge
                        </button>
                      </div>

                      {/* Inline calculateur de marge (violet) */}
                      {inlineEdit[p.id]?.open && (
                        <div className="mt-2 p-2.5 bg-purple-500/10 border border-purple-500/30 rounded-xl space-y-1.5 text-left w-44">
                          {/* Prix HT */}
                          <div className="flex items-center gap-1.5">
                            <span className="text-purple-400 text-[10px] font-bold w-12 shrink-0">Prix HT</span>
                            <input
                              type="number"
                              value={inlineEdit[p.id]?.prixHT || ''}
                              onChange={e => setInlineEdit(prev => ({ ...prev, [p.id]: { ...prev[p.id], prixHT: e.target.value } }))}
                              placeholder="Ex: 45"
                              onClick={e => e.stopPropagation()}
                              className="flex-1 border border-purple-500/40 rounded-lg px-1.5 py-1 text-[11px] text-purple-200 bg-gray-800 focus:outline-none focus:ring-1 focus:ring-purple-400 font-mono min-w-0"
                            />
                            <span className="text-purple-400 text-[10px] shrink-0">€</span>
                          </div>
                          {/* Marge HT € */}
                          <div className="flex items-center gap-1.5">
                            <span className="text-purple-400 text-[10px] font-bold w-12 shrink-0">Marge €</span>
                            <input
                              type="number"
                              value={inlineEdit[p.id]?.marge || ''}
                              onChange={e => setInlineEdit(prev => ({ ...prev, [p.id]: { ...prev[p.id], marge: e.target.value } }))}
                              placeholder="Ex: 30"
                              min="0"
                              onClick={e => e.stopPropagation()}
                              className="flex-1 border border-purple-500/40 rounded-lg px-1.5 py-1 text-[11px] text-purple-200 bg-gray-800 focus:outline-none focus:ring-1 focus:ring-purple-400 font-mono min-w-0"
                            />
                            <span className="text-purple-400 text-[10px] shrink-0">€</span>
                          </div>
                          {/* TTC calculé */}
                          {calcCustomTTC(inlineEdit[p.id]?.prixHT, inlineEdit[p.id]?.marge) && (
                            <div className="flex items-center justify-between px-2 py-1.5 bg-purple-500/20 rounded-lg border border-purple-500/40">
                              <span className="text-purple-400 text-[10px] font-semibold">→ TTC</span>
                              <span className="font-black text-purple-200 text-sm font-mono">
                                {fmt(calcCustomTTC(inlineEdit[p.id]?.prixHT, inlineEdit[p.id]?.marge))}
                              </span>
                            </div>
                          )}
                          {/* Actions */}
                          <div className="flex gap-1.5 pt-0.5">
                            <button
                              onClick={e => saveMargin(e, p)}
                              data-testid={`margin-save-${p.id}`}
                              className="flex-1 bg-purple-500 hover:bg-purple-600 text-white text-[10px] font-bold py-1.5 rounded-lg transition-all">
                              ✓ Enregistrer
                            </button>
                            <button
                              onClick={e => { e.stopPropagation(); setInlineEdit(prev => ({ ...prev, [p.id]: { open: false } })); }}
                              className="px-2 bg-gray-700 hover:bg-gray-600 text-gray-400 text-[10px] rounded-lg border border-gray-600 transition-all">
                              ✕
                            </button>
                          </div>
                        </div>
                      )}
                    </td>
                    {/* Suggestion (calculée) — toujours visible, rouge clair si un prix custom la remplace */}
                    <td className="px-4 py-3.5 text-right border-b border-gray-700">
                      <span className={`font-mono font-bold text-sm ${hasCustom ? 'text-red-400' : 'text-amber-400'}`}>
                        {fmt(p.prix_vente_ttc)}
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-right border-b border-gray-700">
                      <span className={`font-bold text-sm ${margin >= 50 ? 'text-green-400' : margin >= 35 ? 'text-amber-400' : 'text-red-400'}`}>
                        {margin.toFixed(1)}%
                      </span>
                    </td>
                    <td className="px-4 py-3.5 text-right border-b border-gray-700">
                      <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button onClick={e => { e.stopPropagation(); setPrintItem({ product: p, type: null }); }}
                          data-testid={`catalog-print-${p.id}`}
                          className="p-2 text-gray-400 hover:text-blue-400 rounded-lg transition-colors"
                          title="Imprimer étiquette">
                          <Printer size={14} />
                        </button>
                        <button onClick={e => handleDelete(p.id, e)}
                          disabled={deleting === p.id}
                          data-testid={`catalog-delete-${p.id}`}
                          className="p-2 text-gray-300 hover:text-red-500 rounded-lg transition-colors disabled:opacity-40">
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* ── Footer stats ─────────────────────────────────────────────────── */}
        <div className="shrink-0 flex items-center gap-5 px-5 py-3 border-t border-gray-700 bg-gray-800 rounded-b-2xl">
          <span className="text-gray-300 text-sm font-semibold">{filtered.length} fiches</span>
          {filtered.length > 0 && (
            <>
              <span className="text-gray-600">·</span>
              <span className="text-gray-500 text-sm">
                Marge moy. <strong className="text-amber-400">{avgMarge}%</strong>
              </span>
              <span className="text-gray-600">·</span>
              <span className="text-gray-500 text-sm">
                Valeur stock: <strong className="text-gray-300">
                  {fmt(filtered.reduce((a, p) => a + (p.prix_achat_ht || 0), 0))}
                </strong>
              </span>
              <span className="text-gray-600">·</span>
              <span className="text-amber-400 text-xs flex items-center gap-1">
                <Star size={11} /> = Prix personnalisé admin
              </span>
            </>
          )}
          <button onClick={onClose}
            className="ml-auto px-4 py-2 bg-gray-700 hover:bg-gray-600 border border-gray-600 text-gray-200 rounded-xl text-sm font-semibold transition-all">
            Fermer
          </button>
        </div>
      </div>

      <style>{`
        @keyframes pricePulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.6; }
        }
      `}</style>
    </div>

    {/* ── Sélecteur de modèle d'étiquette ──────────────────────────────── */}
    {printItem && printItem.type === null && (
      <div className="fixed inset-0 bg-black/70 z-[60] flex items-center justify-center p-4"
        onClick={() => setPrintItem(null)}>
        <div className="bg-gray-900 border border-gray-700 rounded-2xl p-6 w-full max-w-sm"
          onClick={e => e.stopPropagation()}>
          <p className="text-white font-bold text-base mb-1">Choisir le modèle d'impression</p>
          <p className="text-gray-400 text-sm mb-5">
            {printItem.product.brand_label} {printItem.product.model_label}
          </p>
          <div className="grid grid-cols-2 gap-3">
            <button
              onClick={async () => {
                const p = printItem.product;
                const price = p.prix_vente_custom > 0 ? p.prix_vente_custom : (p.prix_vente_ttc || 0);
                const qLabel = qualityMap[p.quality];
                try {
                  await printCatalogPOS({
                    brand:   p.brand_label,
                    model:   p.model_label,
                    part:    p.part_label,
                    quality: qLabel ? `${qLabel.icon} ${qLabel.label}` : (p.quality || ''),
                    price,
                  });
                  toast.success('Ticket POS ouvert !');
                } catch (e) { toast.error(`Impression: ${e.message}`); }
                setPrintItem(null);
              }}
              data-testid="print-type-repair-btn"
              className="flex flex-col items-center gap-2 p-4 bg-gray-800 hover:bg-orange-500/10 border border-gray-700 hover:border-orange-500 rounded-xl transition-all">
              <Printer size={22} className="text-orange-400" />
              <span className="text-white font-semibold text-sm">Bon Réparation</span>
              <span className="text-gray-500 text-[11px] text-center">Ticket POS 80mm</span>
            </button>
            <button
              onClick={() => setPrintItem(p => ({ ...p, type: 'sale' }))}
              data-testid="print-type-sale-btn"
              className="flex flex-col items-center gap-2 p-4 bg-gray-800 hover:bg-amber-500/10 border border-gray-700 hover:border-amber-500 rounded-xl transition-all">
              <Tag size={22} className="text-amber-400" />
              <span className="text-white font-semibold text-sm">Étiquette Vente</span>
              <span className="text-gray-500 text-[11px] text-center">Clabel 50×30mm</span>
            </button>
          </div>
          <button onClick={() => setPrintItem(null)}
            className="mt-4 w-full py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 text-gray-400 text-sm rounded-xl">
            Annuler
          </button>
        </div>
      </div>
    )}

    {/* ── LabelPreviewModal (vente uniquement) ─────────────────────────── */}
    {printItem?.type === 'sale' && (() => {
      const p   = printItem.product;
      const fin = p.prix_vente_custom > 0 ? p.prix_vente_custom : (p.prix_vente_ttc || 0);
      return (
        <LabelPreviewModal
          type="sale"
          initial={{ brand: p.brand_label, model: p.model_label, priceTTC: Math.round(fin * 100) / 100 }}
          onClose={() => setPrintItem(null)}
        />
      );
    })()}
  </>);
};
